/**
 * Path / arrive helpers around a hard circular obstacle (any large hostile).
 * Genre-neutral — raid uses these for boss today; same math for adds / multi-boss.
 */

/**
 * Closest-point distance from circle center to segment AB < r → intersects.
 */
export function segmentHitsDisk(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  r: number,
): boolean {
  const abx = bx - ax;
  const aby = by - ay;
  const acx = cx - ax;
  const acy = cy - ay;
  const ab2 = abx * abx + aby * aby;
  let t = ab2 < 1e-8 ? 0 : (acx * abx + acy * aby) / ab2;
  t = Math.max(0, Math.min(1, t));
  const px = ax + abx * t;
  const py = ay + aby * t;
  return Math.hypot(px - cx, py - cy) < r;
}

/**
 * Project a goal outside a hard disk of radius `minSep` around center.
 * Bearing: center→goal, or center→from when coincident.
 */
export function clampPointOutsideDisk(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  minSep: number,
  fromX: number,
  fromY: number,
): { x: number; y: number } {
  const dx = x - centerX;
  const dy = y - centerY;
  const len = Math.hypot(dx, dy);
  if (len >= minSep) return { x, y };
  let nx: number;
  let ny: number;
  if (len < 1e-3) {
    const fx = fromX - centerX;
    const fy = fromY - centerY;
    const fl = Math.hypot(fx, fy);
    if (fl < 1e-3) {
      nx = 0;
      ny = 1;
    } else {
      nx = fx / fl;
      ny = fy / fl;
    }
  } else {
    nx = dx / len;
    ny = dy / len;
  }
  return { x: centerX + nx * minSep, y: centerY + ny * minSep };
}

/** Shorter-arc midpoint on a clearance ring around a circle center. */
export function arcWaypointOnCircle(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  centerX: number,
  centerY: number,
  ringR: number,
): { x: number; y: number } {
  const a0 = Math.atan2(fromY - centerY, fromX - centerX);
  const a1 = Math.atan2(toY - centerY, toX - centerX);
  let d = a1 - a0;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  const amid = a0 + d * 0.5;
  return {
    x: centerX + Math.cos(amid) * ringR,
    y: centerY + Math.sin(amid) * ringR,
  };
}

export type DashAroundHardDiskOpts = {
  /** Extra radius on the via ring (default 4). */
  viaRingPad?: number;
  /** Optional post-project clamp (arena bounds). */
  clamp?: (x: number, y: number) => { x: number; y: number };
};

/**
 * Dash plan around a hard disk: clamp final outside minSep; if the straight
 * segment clips the disk, first leg is a shorter-arc waypoint on the clearance ring.
 */
export function planDashAroundHardDisk(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  centerX: number,
  centerY: number,
  minSep: number,
  opts?: DashAroundHardDiskOpts,
): { legX: number; legY: number; finalX: number; finalY: number } {
  const clamp = opts?.clamp ?? ((x: number, y: number) => ({ x, y }));
  const viaPad = opts?.viaRingPad ?? 4;
  const outside = clampPointOutsideDisk(
    toX,
    toY,
    centerX,
    centerY,
    minSep,
    fromX,
    fromY,
  );
  const final = clamp(outside.x, outside.y);
  if (
    !segmentHitsDisk(
      fromX,
      fromY,
      final.x,
      final.y,
      centerX,
      centerY,
      minSep - 0.5,
    )
  ) {
    return { legX: final.x, legY: final.y, finalX: final.x, finalY: final.y };
  }
  const viaRaw = arcWaypointOnCircle(
    fromX,
    fromY,
    final.x,
    final.y,
    centerX,
    centerY,
    minSep + viaPad,
  );
  const via = clamp(viaRaw.x, viaRaw.y);
  const viaOut = clampPointOutsideDisk(
    via.x,
    via.y,
    centerX,
    centerY,
    minSep,
    fromX,
    fromY,
  );
  return {
    legX: viaOut.x,
    legY: viaOut.y,
    finalX: final.x,
    finalY: final.y,
  };
}

/**
 * Arrive / clear when pressed on a hard disk with an inward remaining vector.
 * Far-side destinations on another bearing must NOT clear (path-around).
 */
export function dashArrivedOrHardBlocked(
  bodyX: number,
  bodyY: number,
  goalX: number,
  goalY: number,
  centerX: number,
  centerY: number,
  minSep: number,
  arrive: number,
  finalDest?: { x: number; y: number } | null,
): boolean {
  const dist = (ax: number, ay: number, bx: number, by: number) =>
    Math.hypot(ax - bx, ay - by);
  if (dist(bodyX, bodyY, goalX, goalY) < arrive) return true;
  const dBody = dist(bodyX, bodyY, centerX, centerY);
  if (dBody > minSep + arrive) return false;
  const gD = dist(goalX, goalY, centerX, centerY);
  if (gD < minSep - 1e-3) return true;
  const toGx = goalX - bodyX;
  const toGy = goalY - bodyY;
  const toGlen = Math.hypot(toGx, toGy);
  if (toGlen < 1e-3) return true;
  const outX = bodyX - centerX;
  const outY = bodyY - centerY;
  const outLen = Math.hypot(outX, outY) || 1;
  const inward =
    -((toGx / toGlen) * (outX / outLen) + (toGy / toGlen) * (outY / outLen));
  if (inward <= 0.15) return false;
  const fx = finalDest?.x ?? goalX;
  const fy = finalDest?.y ?? goalY;
  const fD = dist(fx, fy, centerX, centerY);
  if (fD >= minSep - 1e-3) {
    const fdx = fx - centerX;
    const fdy = fy - centerY;
    const fLen = Math.hypot(fdx, fdy) || 1;
    const aligned =
      (outX / outLen) * (fdx / fLen) + (outY / outLen) * (fdy / fLen);
    if (aligned < 0.5) return false;
  }
  return true;
}
